// src/components/products/index.jsx

import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  CubeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';

import { useAuth } from '../../contexts/AuthContext'; 
import {
  getJewellerRings,
  createRing,
  updateRing,
  deleteRing,
} from '../../services/product';
import { uploadProductImages } from '../../services/storage';

import ProductListItem from './components/ProductListItem';
import ProductForm from './components/ProductForm';
import DeleteModal from './components/DeleteModal';

// Add styles using CSS variables
const styles = `
  .page-container {
    min-height: 100vh;
    background-color: var(--Surface-Secondary);
    font-family: var(--font-family-primary);
  }

  /* UPDATED STYLE */
  .sort-dropdown {
    /* Remove native arrow */
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    /* Add custom arrow */
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
    background-position: right var(--spacing-s) center;
    background-repeat: no-repeat;
    background-size: 1.25em;

    /* Moved from inline styles */
    background-color: var(--Surface-Primary);
    border: 1px solid var(--Stroke-Light);
    border-radius: var(--radius-pill);
    color: var(--Text-Secondary);
    font-size: var(--font-size-body4);
    font-weight: var(--font-weight-medium);
    padding: var(--spacing-xs) var(--spacing-xl) var(--spacing-xs) var(--spacing-s); /* Top, Right, Bottom, Left */
    cursor: pointer;
    outline: none;
  }

  .content-wrapper {
    max-width: var(--content-max-width);
    margin: 0 auto;

  }

  .header-card {
    background: white;
    padding: var(--spacing-xl);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-frame-big);
    margin-bottom: var(--spacing-l);
    border: var(--card-border);
  }

  .header-title {
    font-size: var(--font-size-h4);
    line-height: var(--line-height-h4);
    font-weight: var(--font-weight-h4);
    color: var(--Grey-800);
    margin: 0;
  }

  .header-subtitle {
    color: var(--Grey-800);
    opacity: 0.8;
    margin-top: var(--spacing-xxs);
    font-size: var(--font-size-body3);
    line-height: var(--line-height-body3);
  }

  /* Container should have position: relative for the icon positioning */
  .search-container {
    position: relative;
    display: inline-block;
  }

  .search-input {
    padding: var(--spacing-s) var(--spacing-s) var(--spacing-s) var(--spacing-xxl);
    background: var(--Surface-Primary);
    border: 1px solid var(--Stroke-Secondary);
    border-radius: var(--radius-xl);
    color: var(--Text-Primary);
    font-size: var(--font-size-body3);
    min-width: 250px;
    transition: all 0.2s ease;
  }

  .search-input:focus {
    outline: none;
    box-shadow: var(--Input-Focus-Ring);
    border-color: var(--Purple-500);
    background: var(--Surface-Primary);
  }

  .search-input::placeholder {
    color: var(--Text-Tertiary);
  }

  .search-icon {
    position: absolute;
    left: var(--spacing-s);
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    color: var(--Text-Secondary);
    pointer-events: none;
  }

  .btn-primary {
      padding: var(--button-padding-y) var(--button-padding-x);
      background: var(--brand-gradient-1);
      color: white;
      border: none;
      border-radius: var(--button-radius);
      font-size: var(--font-size-button);
      font-weight: var(--button-font-weight);
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: var(--spacing-xs);
      box-shadow: var(--shadow-lg);
      transition: all 0.2s ease;
  }

  .btn-primary:hover {
    background: var(--Purple-400);
    box-shadow: var(--shadow-xl);
    transform: translateY(-1px);
  }

  .btn-secondary {
    padding: var(--button-padding-y) var(--button-padding-x);
    background: var(--Surface-Primary);
    color: var(--Purple-700);
    border: none;
    border-radius: var(--button-radius);
    font-size: var(--font-size-button);
    font-weight: var(--button-font-weight);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-xs);
    box-shadow: var(--shadow-lg);
    transition: all 0.2s ease;
  }

  .btn-secondary:hover {
    background: rgba(255, 255, 255, 0.95);
    box-shadow: var(--shadow-xl);
    transform: translateY(-1px);
  }  

  .main-card {
    background: var(--Surface-Primary);
    border-radius: var(--radius-xl);
    box-shadow: var(--card-shadow);
    border: var(--card-border);
    overflow: hidden;
  }

  .new-product-card {
    background: var(--Surface-Primary);
    border-radius: var(--radius-m);
    box-shadow: var(--card-shadow);
    border: var(--card-border);
    overflow: hidden;
  }

  .tabs-container {
    border-bottom: 1px solid var(--Stroke-Light);
    background: var(--Surface-Primary);
  }

  .tabs-wrapper {
    display: flex;
    gap: var(--spacing-xl);
  }

  .tab-button {
    padding: var(--spacing-s) var(--spacing-xxs);
    border: none;
    background: transparent;
    font-family: var(--font-family-primary);
    font-size: var(--font-size-body4);
    font-weight: var(--font-weight-medium);
    color: var(--Text-Secondary);
    cursor: pointer;
    transition: color 0.2s ease;
    position: relative;
    text-transform: capitalize;
  }

  .tab-button.active {
    color: var(--Purple-700);
  }

  .tab-button:hover:not(.active) {
    color: var(--Text-Primary);
  }

  .tab-indicator {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--Purple-700);
  }

  .tab-badge {
    margin-left: var(--spacing-xs);
    padding: 2px var(--spacing-xs);
    border-radius: var(--radius-pill);
    font-size: var(--font-size-body5);
    font-weight: var(--font-weight-normal);
  }

  .tab-badge.active {
    background: var(--Purple-100);
    color: var(--Purple-700);
  }

  .tab-badge:not(.active) {
    background: var(--Grey-100);
    color: var(--Text-Secondary);
  }

  .content-area {
    padding: var(--spacing-l);
  }

  .loading-container {
    text-align: center;
    padding: var(--spacing-xxxl) 0;
  }

  .loading-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--Purple-500);
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .loading-text {
    color: var(--Text-Secondary);
    font-size: var(--font-size-body3);
    margin-left: var(--spacing-s);
  }

  .error-container {
    text-align: center;
    padding: var(--spacing-xxxl) 0;
  }

  .error-box {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-s);
    padding: var(--spacing-l);
    background: var(--Surface-Error);
    border-radius: var(--radius-s);
    border: 1px solid var(--Stroke-Error);
  }

  .error-icon {
    width: 48px;
    height: 48px;
    color: var(--Red-600);
  }

  .error-text {
    color: var(--Text-Error);
    font-size: var(--font-size-body3);
  }

  .empty-state {
    text-align: center;
    padding: var(--spacing-xxxl) var(--spacing-xl);
  }

  .empty-icon {
    width: 64px;
    height: 64px;
    color: var(--Grey-300);
    margin: 0 auto var(--spacing-m);
  }

  .empty-title {
    color: var(--Text-Secondary);
    font-size: var(--font-size-body2);
    font-weight: var(--font-weight-medium);
    margin-bottom: var(--spacing-xs);
  }

  .empty-subtitle {
    color: var(--Text-Tertiary);
    font-size: var(--font-size-body4);
  }

  .form-header {
    padding: var(--spacing-m) var(--spacing-l);
    background: var(--brand-gradient-4);
    border-bottom: 1px solid var(--Purple-100);
  }

  .form-title {
    font-size: var(--font-size-body2);
    font-weight: var(--font-weight-semibold);
    color: var(--Grey-900);
  }

  .products-list {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
  }

  .walkthrough-row {
    margin-top: var(--spacing-s);
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-xs);
  }

  .walkthrough-button {
    background: transparent;
    border: none;
    padding: 0;
    color: var(--Text-Secondary);
    font-size: var(--font-size-body4);
    font-weight: var(--font-weight-medium);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-xxs);
    transition: var(--transition-all);
  }

  .walkthrough-button:hover {
    color: var(--Text-Primary);
  }

  .walkthrough-icon {
    width: 1rem;
    height: 1rem;
  }

  .walkthrough-duration {
    font-size: var(--font-size-body5);
    color: var(--Text-Tertiary);
  }

  .video-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(9, 11, 15, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: var(--spacing-l);
  }

  .video-modal {
    width: min(900px, 100%);
    background: var(--Surface-Primary);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-lg);
    border: var(--card-border);
    overflow: hidden;
  }

  .video-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-l);
    border-bottom: var(--card-border);
  }

  .video-modal-title {
    font-size: var(--font-size-body2);
    font-weight: var(--font-weight-medium);
    color: var(--Text-Primary);
  }

  .video-modal-close {
    background: transparent;
    border: none;
    color: var(--Text-Secondary);
    cursor: pointer;
    font-size: 1.2rem;
  }

  .video-frame {
    position: relative;
    padding-top: 56.25%;
    background: var(--Grey-900);
  }

  .video-frame iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const ProductsPage = () => {
  const { currentUser } = useAuth();

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filteredProducts, setFilteredProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, productId: null, productName: '' });

  const [isVideoOpen, setIsVideoOpen] = useState(false);

  useEffect(() => {
    if (!isVideoOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsVideoOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVideoOpen]);

  // 1. ADD NEW STATE
  const [sortBy, setSortBy] = useState('dateAdded');

  useEffect(() => {
    const fetchProducts = async () => {
      if (!currentUser) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const fetchedProducts = await getJewellerRings(currentUser.uid);
        
        // DEBUG: Check what we're getting from Firestore
        console.log('=== PRODUCTS DEBUG ===');
        console.log('Fetched products count:', fetchedProducts.length);
        if (fetchedProducts.length > 0) {
          console.log('First product:', fetchedProducts[0]);
          console.log('Available fields:', Object.keys(fetchedProducts[0]));
          console.log('First product createdAt:', fetchedProducts[0].createdAt);
          console.log('First product lastUpdated:', fetchedProducts[0].lastUpdated);
          console.log('createdAt type:', typeof fetchedProducts[0].createdAt);
        }
        
        setProducts(fetchedProducts);
      } catch (err) {
        setError('Failed to fetch products. Please try again later.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [currentUser]);

  // 2. UPDATE THE useEffect HOOK (replace the existing filter useEffect)
  useEffect(() => {
    let filtered = [...products];
    if (activeTab === 'live') filtered = filtered.filter(p => p.status === 'live');
    if (activeTab === 'draft') filtered = filtered.filter(p => p.status === 'draft');
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.metalType?.toLowerCase().includes(query)
      );
    }
    // Helper function to convert Firestore Timestamp to Date
    const getDateFromTimestamp = (timestamp) => {
      if (!timestamp) return new Date(0);
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      } else if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000);
      }
      return new Date(timestamp);
    };
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'nameAsc':
          return (a.name || '').localeCompare(b.name || '');
        case 'nameDesc':
          return (b.name || '').localeCompare(a.name || '');
        case 'priceHighLow':
          return (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0);
        case 'priceLowHigh':
          return (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0);
        case 'dateAdded':
        default:
          return getDateFromTimestamp(b.createdAt) - getDateFromTimestamp(a.createdAt);
      }
    });
    setFilteredProducts(filtered);
  }, [products, activeTab, searchQuery, sortBy]);

  const handleSaveProduct = async (productDataFromForm) => {
    if (!currentUser) {
      setError("You must be logged in to save products.");
      return;
    }

    const { newImageFiles, ...dataToSave } = productDataFromForm;

    try {
      setError(null);
      let uploadedImageUrls = [];

      if (newImageFiles && newImageFiles.length > 0) {
        const productId = showNewForm ? 'new' : dataToSave.id;
        uploadedImageUrls = await uploadProductImages(currentUser.uid, productId, newImageFiles);
      }

      const finalImageUrls = [...(dataToSave.imageUrls || []), ...uploadedImageUrls];
      const finalProductData = { ...dataToSave, imageUrls: finalImageUrls };

      if (showNewForm) {
        const newProductId = await createRing(currentUser.uid, finalProductData);
        setProducts([{ ...finalProductData, id: newProductId }, ...products]);
        setShowNewForm(false);
        setError(null);
      } else {
        await updateRing(finalProductData.id, finalProductData);
        setProducts(products.map(p =>
          p.id === finalProductData.id ? finalProductData : p
        ));
        setExpandedProduct(null);
        setError(null);
      }
    } catch (err) {
      console.error("Failed to save product:", err);
      setError("Failed to save the product. Please check the console for details.");
    }
  };
  
  const handleDeleteProduct = (productId) => {
    const product = products.find(p => p.id === productId);
    setDeleteModal({ isOpen: true, productId, productName: product?.name || '' });
  };
  
  const confirmDelete = async () => {
    try {
      await deleteRing(deleteModal.productId);
      setProducts(products.filter(p => p.id !== deleteModal.productId));
      setDeleteModal({ isOpen: false, productId: null, productName: '' });
      setExpandedProduct(null);
    } catch (err) {
       console.error("Failed to delete product:", err);
    }
  };

  const toggleStatus = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    const newStatus = product.status === 'live' ? 'draft' : 'live';
    try {
      await updateRing(productId, { ...product, status: newStatus });
      setProducts(products.map(p =>
        p.id === productId ? { ...p, status: newStatus } : p
      ));
    } catch (err) {
      console.error("Failed to toggle status:", err);
    }
  };
  
  const handleExpand = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const counts = {
    all: products.length,
    live: products.filter(p => p.status === 'live').length,
    draft: products.filter(p => p.status === 'draft').length,
  };

  const walkthrough = {
    title: 'How the Products page works',
    durationLabel: '3 min',
    embedUrl: 'https://player.vimeo.com/video/1170341834?badge=0&autopause=0&player_id=0&app_id=58479'
  };
  
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="loading-container">
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-s)' }}>
            <div className="loading-spinner" />
            <span className="loading-text">Loading products...</span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-container">
          <div className="error-box">
            <ExclamationTriangleIcon className="error-icon" />
            <p className="error-text">{error}</p>
          </div>
        </div>
      );
    }

    if (showNewForm) {
      return (
        <div className="new-product-card" style={{ overflow: 'hidden' }}>
          <div className="form-header">
            <h3 className="form-title">Create New Product (engagement rings only)</h3>
          </div>
          <ProductForm
            isNew={true}
            onSave={handleSaveProduct}
            onCancel={() => setShowNewForm(false)}
          />
        </div>
      );
    }

    if (filteredProducts.length === 0) {
      return (
        <div className="empty-state">
          <CubeIcon className="empty-icon" />
          <p className="empty-title">No products found</p>
          <p className="empty-subtitle">
            {searchQuery ? 'Try adjusting your search' : 'Click "Add Product" to create your first product'}
          </p>
        </div>
      );
    }

    return (
      <div className="products-list">
        {filteredProducts.map((product) => (
          <ProductListItem
            key={product.id}
            product={product}
            isExpanded={expandedProduct === product.id}
            onExpand={handleExpand}
            onSave={handleSaveProduct}
            onDelete={handleDeleteProduct}
            onToggleStatus={toggleStatus}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <style>{styles}</style>
      <div className="page-container">
        <div className="content-wrapper">
          {/* Header */}
          <div className="header-card">
            <div style={{ 
              display: 'flex', 
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--spacing-m)',
              flexWrap: 'wrap'
            }}>
              <div>
                <h1 className="header-title">Your Showcase Rings</h1>
                <div className="walkthrough-row">
                  <button
                    type="button"
                    className="walkthrough-button"
                    onClick={() => setIsVideoOpen(true)}
                  >
                    <svg
                      className="walkthrough-icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M8 5.14v13.72a1 1 0 0 0 1.55.83l9.2-6.86a1 1 0 0 0 0-1.66l-9.2-6.86A1 1 0 0 0 8 5.14z" />
                    </svg>
                    Watch walkthrough
                  </button>
                  {walkthrough.durationLabel && (
                    <span className="walkthrough-duration">
                      {walkthrough.durationLabel}
                    </span>
                  )}
                </div>
                 {isVideoOpen && (
                  <div
                    className="video-modal-overlay"
                    onClick={() => setIsVideoOpen(false)}
                  >
                    <div
                      className="video-modal"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className="video-modal-header">
                        <div className="video-modal-title">
                          {walkthrough.title || 'Products walkthrough'}
                        </div>
                        <button
                          type="button"
                          className="video-modal-close"
                          onClick={() => setIsVideoOpen(false)}
                          aria-label="Close video"
                        >
                          ✕
                        </button>
                      </div>

                      <div className="video-frame">
                        <iframe
                          src={walkthrough.embedUrl}
                          title={walkthrough.title || 'Products walkthrough'}
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'row',
                gap: 'var(--spacing-s)',
                alignItems: 'center'
              }}>
                <div style={{ position: 'relative' }}>
                  <MagnifyingGlassIcon className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
                <button
                  onClick={() => { setShowNewForm(true); setExpandedProduct(null); }}
                  className="btn-primary"
                >
                  <PlusIcon style={{ width: '20px', height: '20px' }} />
                  Add Product
                </button>
              </div>
            </div>
          </div>

          {/* Main Content Card */}
          <div className="main-card">
            {/* Tabs */}
            <div className="tabs-container">
              <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '0 var(--spacing-l)'
                }}>
                  <div className="tabs-wrapper" style={{ padding: 0 }}>
                    {['all', 'live', 'draft'].map(tab => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`tab-button ${activeTab === tab ? 'active' : ''}`}
                      >
                        <span style={{ position: 'relative' }}>
                          {tab}
                          <span className={`tab-badge ${activeTab === tab ? 'active' : ''}`}>
                            {counts[tab]}
                          </span>
                        </span>
                        {activeTab === tab && (
                          <div className="tab-indicator" />
                        )}
                      </button>
                    ))}
                  </div>
                  
                  {/* Sort Dropdown */}
                  <div style={{ paddingRight: 'var(--spacing-m)' }}>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="sort-dropdown"
                    >
                      <option value="dateAdded">Newest First</option>
                      <option value="nameAsc">Name A-Z</option>
                      <option value="nameDesc">Name Z-A</option>
                      <option value="priceHighLow">Price High-Low</option>
                      <option value="priceLowHigh">Price Low-High</option>
                    </select>
                  </div>
                </div>
            </div>

            {/* Content */}
            <div className="content-area">
              {renderContent()}
            </div>
          </div>
        </div>

        <DeleteModal
          isOpen={deleteModal.isOpen}
          onClose={() => setDeleteModal({ isOpen: false, productId: null, productName: '' })}
          onConfirm={confirmDelete}
          productName={deleteModal.productName}
        />
      </div>
    </>
  );
};

export default ProductsPage;
